import {
  Anchor,
  Badge,
  Box,
  Breadcrumbs as MantineBreadcrumbs,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBook2,
  IconCalculator,
  IconLink,
  IconSchool,
} from "@tabler/icons-react";
import { ReactNode, useEffect } from "react";
import { Link } from "react-router-dom";
import { logEvent } from "../../../Services/analytics.service";
import { ToolCatalogItem, ToolRelatedLink } from "../../../data/toolsCatalog";
import Seo from "../../../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../../seo/utils";
import "./ToolPageLayout.css";

export interface ToolFaqItem {
  question: string;
  answer: string;
}

export interface ToolTextSection {
  title: string;
  body: ReactNode;
}

export interface ToolPageLayoutProps {
  title: string;
  description: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  path: string;
  category: string;
  seoTitle: string;
  metaDescription: string;
  toolComponent: ReactNode;
  howToUseSteps: string[];
  exampleContent: ToolTextSection;
  formulaSection?: ToolTextSection;
  whySection: ToolTextSection;
  commonMistakes?: string[];
  faqItems: ToolFaqItem[];
  relatedTools: ToolCatalogItem[];
  relatedBlogs: ToolRelatedLink[];
  relatedServices: ToolRelatedLink[];
  ctaTitle: string;
  ctaDescription: string;
  ctaButtonText: string;
  ctaButtonHref: string;
  schemaData?: Record<string, unknown>;
}

export function createToolSchema({
  title,
  description,
  path,
  schemaData,
}: Pick<
  ToolPageLayoutProps,
  "title" | "description" | "path" | "schemaData"
>) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: title,
    description,
    applicationCategory: "EducationalApplication",
    operatingSystem: "Web",
    url: buildCanonicalUrl(path),
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    isAccessibleForFree: true,
    publisher: {
      "@type": "Organization",
      name: "SubjectBuddy",
    },
    ...schemaData,
  };
}

export function trackToolEvent(
  eventName: string,
  eventData: Record<string, unknown>,
) {
  logEvent(eventName, eventData);
}

export function ToolPageLayout({
  title,
  description,
  primaryKeyword,
  secondaryKeywords,
  path,
  category,
  seoTitle,
  metaDescription,
  toolComponent,
  howToUseSteps,
  exampleContent,
  formulaSection,
  whySection,
  commonMistakes,
  faqItems,
  relatedTools,
  relatedBlogs,
  relatedServices,
  ctaTitle,
  ctaDescription,
  ctaButtonText,
  ctaButtonHref,
  schemaData,
}: ToolPageLayoutProps) {
  useEffect(() => {
    trackToolEvent("tool_view", {
      tool_name: title,
      tool_category: category,
      primary_keyword: primaryKeyword,
    });
  }, [category, primaryKeyword, title]);

  return (
    <Container fluid className="tool-page" px={0}>
      <Seo
        title={seoTitle}
        description={metaDescription}
        path={path}
        ogTitle={title}
        ogDescription={description}
        keywords={[primaryKeyword, ...secondaryKeywords]}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: "/tools" },
            { name: title, path },
          ]),
          createFaqSchema(faqItems),
          createToolSchema({ title, description, path, schemaData }),
        ]}
      />

      <ToolHero
        title={title}
        description={description}
        primaryKeyword={primaryKeyword}
        category={category}
      >
        {toolComponent}
      </ToolHero>

      <Container size="xl" py={48}>
        <Stack gap={34} className="tool-content">
          <section className="tool-section">
            <Stack gap="md">
              <Title order={2}>How to Use This {title}</Title>
              <ol className="tool-steps">
                {howToUseSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </Stack>
          </section>

          {formulaSection ? (
            <section className="tool-section">
              <Stack gap="md">
                <Title order={2}>{formulaSection.title}</Title>
                <Box className="tool-rich-text">{formulaSection.body}</Box>
              </Stack>
            </section>
          ) : null}

          <section className="tool-section">
            <Stack gap="md">
              <Title order={2}>{exampleContent.title}</Title>
              <Box className="tool-rich-text">{exampleContent.body}</Box>
            </Stack>
          </section>

          <ToolCTA
            title={ctaTitle}
            description={ctaDescription}
            buttonText={ctaButtonText}
            href={ctaButtonHref}
            toolName={title}
            category={category}
            primaryKeyword={primaryKeyword}
            compact
          />

          <section className="tool-section">
            <Stack gap="md">
              <Title order={2}>{whySection.title}</Title>
              <Box className="tool-rich-text">{whySection.body}</Box>
              {commonMistakes?.length ? (
                <Paper withBorder radius="md" p="lg" className="tool-note-box">
                  <Stack gap="sm">
                    <Title order={3}>Common mistakes to avoid</Title>
                    <ul className="tool-list">
                      {commonMistakes.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </Stack>
                </Paper>
              ) : null}
            </Stack>
          </section>

          <RelatedTools tools={relatedTools} />
          <RelatedBlogs links={relatedBlogs} />
          <RelatedServices links={relatedServices} />

          <ToolCTA
            title={ctaTitle}
            description={ctaDescription}
            buttonText={ctaButtonText}
            href={ctaButtonHref}
            toolName={title}
            category={category}
            primaryKeyword={primaryKeyword}
          />

          <FAQSection title={`${title} FAQs`} items={faqItems} />
        </Stack>
      </Container>
    </Container>
  );
}

export function ToolHero({
  title,
  description,
  primaryKeyword,
  category,
  children,
}: {
  title: string;
  description: string;
  primaryKeyword: string;
  category: string;
  children: ReactNode;
}) {
  return (
    <Box className="tool-hero">
      <Container size="xl">
        <Stack gap="lg">
          <Breadcrumbs current={title} />
          <Group gap="sm">
            <Badge color="green" radius="sm" size="lg">
              {category}
            </Badge>
            <Badge color="blue" radius="sm" size="lg" variant="light">
              {primaryKeyword}
            </Badge>
          </Group>
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={34}>
            <Stack gap="md" justify="center">
              <ThemeIcon size={58} radius="md" color="green" variant="light">
                <IconCalculator size={32} />
              </ThemeIcon>
              <Title order={1} className="tool-title">
                {title}
              </Title>
              <Text size="lg" c="dimmed" className="tool-intro">
                {description}
              </Text>
              <Text size="sm" fw={700} c="green">
                Get expert academic guidance from SubjectBuddy.
              </Text>
            </Stack>
            <Paper
              withBorder
              radius="md"
              p={{ base: "md", md: "xl" }}
              className="tool-panel"
            >
              {children}
            </Paper>
          </SimpleGrid>
        </Stack>
      </Container>
    </Box>
  );
}

export function Breadcrumbs({ current }: { current: string }) {
  return (
    <MantineBreadcrumbs separator="/">
      <Anchor component={Link} to="/">
        Home
      </Anchor>
      <Anchor component={Link} to="/tools">
        Tools
      </Anchor>
      <Text>{current}</Text>
    </MantineBreadcrumbs>
  );
}

export function ToolCard({ tool }: { tool: ToolCatalogItem }) {
  const primaryService = tool.relatedServices[0];

  return (
    <Paper
      component={Link}
      to={tool.url}
      withBorder
      radius="md"
      p="lg"
      className="tool-card"
    >
      <Stack gap="sm" h="100%">
        <Group justify="space-between" align="flex-start">
          <Badge color="green" variant="light" radius="sm">
            {tool.category}
          </Badge>
          <IconArrowRight size={20} />
        </Group>
        <Title order={3}>{tool.title}</Title>
        <Text size="sm" c="dimmed" className="tool-card-description">
          {tool.description}
        </Text>
        <Text size="xs" tt="uppercase" fw={800} c="blue">
          {tool.primaryKeyword}
        </Text>
        <Group justify="space-between" mt="auto">
          <Text size="sm" fw={800} c="green">
            Use Tool
          </Text>
          {primaryService ? (
            <Text size="xs" c="dimmed">
              Related: {primaryService.title}
            </Text>
          ) : null}
        </Group>
      </Stack>
    </Paper>
  );
}

export function ToolCTA({
  title,
  description,
  buttonText,
  href,
  toolName,
  category,
  primaryKeyword,
  compact = false,
}: {
  title: string;
  description: string;
  buttonText: string;
  href: string;
  toolName: string;
  category: string;
  primaryKeyword: string;
  compact?: boolean;
}) {
  return (
    <Paper
      withBorder
      radius="md"
      p={compact ? "lg" : "xl"}
      className={compact ? "tool-cta is-compact" : "tool-cta"}
    >
      <Group justify="space-between" align="center" gap="lg">
        <Group gap="md" align="flex-start" wrap="nowrap">
          <ThemeIcon radius="md" size={compact ? 42 : 54} color="blue">
            <IconSchool size={compact ? 22 : 28} />
          </ThemeIcon>
          <Stack gap={4}>
            <Title order={compact ? 3 : 2}>{title}</Title>
            <Text c="dimmed" className="tool-cta-copy">
              {description}
            </Text>
            <Text size="sm" c="green" fw={700}>
              Get expert academic guidance from SubjectBuddy.
            </Text>
          </Stack>
        </Group>
        <Button
          component={Link}
          to={href}
          radius="md"
          rightSection={<IconArrowRight size={18} />}
          onClick={() =>
            trackToolEvent("tool_cta_click", {
              tool_name: toolName,
              tool_category: category,
              primary_keyword: primaryKeyword,
              cta_target: href,
            })
          }
        >
          {buttonText}
        </Button>
      </Group>
    </Paper>
  );
}

export function FAQSection({
  title,
  items,
}: {
  title: string;
  items: ToolFaqItem[];
}) {
  return (
    <section className="tool-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {items.map((faq) => (
          <Paper key={faq.question} withBorder radius="md" p="lg">
            <Stack gap={6}>
              <Title order={3}>{faq.question}</Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                {faq.answer}
              </Text>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </section>
  );
}

export function RelatedTools({ tools }: { tools: ToolCatalogItem[] }) {
  if (!tools.length) {
    return null;
  }

  return (
    <section className="tool-section">
      <Stack gap="md">
        <Title order={2}>Related Tools</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {tools.map((tool) => (
            <Paper
              key={tool.slug}
              component={Link}
              to={tool.url}
              withBorder
              radius="md"
              p="lg"
              className="tool-related-card"
            >
              <Group gap="md" wrap="nowrap" align="flex-start">
                <ThemeIcon color="green" variant="light" radius="md">
                  <IconCalculator size={20} />
                </ThemeIcon>
                <Stack gap={4}>
                  <Text fw={800}>{tool.title}</Text>
                  <Text size="sm" c="dimmed">
                    {tool.description}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </section>
  );
}

export function RelatedBlogs({ links }: { links: ToolRelatedLink[] }) {
  if (!links.length) {
    return null;
  }

  return (
    <section className="tool-section">
      <Stack gap="md">
        <Title order={2}>Related Blogs</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {links.map((link) => (
            <Paper
              key={link.url}
              component={Link}
              to={link.url}
              withBorder
              radius="md"
              p="lg"
              className="tool-related-card"
            >
              <Group gap="md" wrap="nowrap">
                <ThemeIcon color="blue" variant="light" radius="md">
                  <IconBook2 size={20} />
                </ThemeIcon>
                <Text fw={800}>{link.title}</Text>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </section>
  );
}

export function RelatedServices({ links }: { links: ToolRelatedLink[] }) {
  if (!links.length) {
    return null;
  }

  return (
    <section className="tool-section">
      <Stack gap="md">
        <Title order={2}>Related Student Support</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {links.map((link) => (
            <Paper
              key={link.url}
              component={Link}
              to={link.url}
              withBorder
              radius="md"
              p="lg"
              className="tool-related-card"
            >
              <Group gap="md" wrap="nowrap">
                <ThemeIcon color="green" variant="light" radius="md">
                  <IconLink size={20} />
                </ThemeIcon>
                <Text fw={800}>{link.title}</Text>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </section>
  );
}
